import { GithubAuth } from "@backstage/core-app-api";
import {
    ApiBlueprint,
    configApiRef,
    coreExtensionData,
    createApiFactory,
    createExtension,
    createExtensionInput,
    createFrontendModule,
    discoveryApiRef,
    githubAuthApiRef,
    oauthRequestApiRef,
} from "@backstage/frontend-plugin-api";
import {
    ScmAuth,
    ScmIntegrationsApi,
    scmIntegrationsApiRef,
} from "@backstage/integration-react";
import {
    NavContentBlueprint,
    SignInPageBlueprint,
} from "@backstage/plugin-app-react";
import { AuthSignInPage } from "@internal/backstage-plugin-app-react";

import { AppLayout } from "./components/AppLayout";
import { SidebarNav } from "./components/SidebarNav";

/**
 * App layout extension.
 *
 * Overrides the built-in `app/layout` extension, which by default renders
 * Material UI's `SidebarPage` from `@backstage/core-components`. Overriding it
 * is the only way to get an MUI-free app shell: `SidebarPage` owns the flex
 * container, the pinned-sidebar content offset and the pin state context, so
 * replacing only the rail would have left MUI in the shell regardless.
 *
 * Backstage documents the override *mechanism* but not this *target* — see
 * "Override built-in extensions" in the built-in extensions guide, and the
 * extension overrides architecture doc, which sanctions overriding by
 * declaring a new extension with the same resulting id:
 *
 * > You can also create new extensions with `createExtension` or a blueprint
 * > that are either completely net-new extensions, or override an existing
 * > extension by using the same `kind`, `namespace` and `name` to produce the
 * > same extension ID.
 *
 * We use that form rather than `appPlugin.getExtension("app/layout")
 * .override(...)` to keep `@backstage/plugin-app` out of this package's
 * *runtime* dependencies: the module declares only the id it is replacing,
 * rather than importing the plugin that owns it. (It is a devDependency, so
 * `module.test.tsx` can build a real app tree and assert the override lands.)
 *
 * That choice has a real cost, and it is why the test exists. `.override()`
 * would be checked at compile time — `plugin-app` types its extension map with
 * a literal `"app/layout"` key — and would inherit upstream's inputs and
 * output rather than restating them. Here both are hand-written strings that
 * nothing type-checks, and the two most likely upstream changes fail quietly:
 * a new input on `app/layout` is only a `console.warn`, and a changed
 * `attachTo` is silently won by this module. A rename is the one case that
 * fails loudly, and `module.test.tsx` covers it.
 *
 * The `nav` and `content` inputs mirror the upstream declaration exactly, so
 * `app/nav` and `app/routes` — which hard-attach to them — keep resolving.
 *
 * @see {@link AppLayout} for what replaces `SidebarPage`.
 */
const appLayout = createExtension({
    name: "layout",
    attachTo: { id: "app/root", input: "children" },
    inputs: {
        nav: createExtensionInput([coreExtensionData.reactElement], {
            singleton: true,
        }),
        content: createExtensionInput([coreExtensionData.reactElement], {
            singleton: true,
        }),
    },
    output: [coreExtensionData.reactElement],
    factory: ({ inputs }) => [
        coreExtensionData.reactElement(
            <AppLayout
                nav={inputs.nav.get(coreExtensionData.reactElement)}
                content={inputs.content.get(coreExtensionData.reactElement)}
            />
        ),
    ],
});

/**
 * Sidebar nav content extension.
 *
 * Overrides the default `nav-content:app` extension with Banno's custom
 * {@link SidebarNav} component, which includes:
 * - Banno logo (collapsed/expanded)
 * - Environment metadata display
 * - Catalog submenu (Components, APIs, Templates, Groups, Users)
 * - Docs, Soundcheck, Templates, and More (Announcements, Entity Validator)
 * - Tools submenu (Firefighter, OneShotSQL, Secret Sharing, K8s Dashboard)
 * - Settings (Notifications, DevTools, RBAC, User Settings)
 */
const SidebarNavContent = NavContentBlueprint.make({
    params: {
        component: ({ navItems }) => <SidebarNav navItems={navItems} />,
    },
});

/**
 * Sign-in page extension.
 *
 * Overrides the default `sign-in-page:app` extension with Banno's custom
 * {@link AuthSignInPage} component, which shows an Okta auth provider and
 * optionally a guest provider (when configured for local development).
 */
const signInPage = SignInPageBlueprint.make({
    params: {
        loader: async () => AuthSignInPage,
    },
});

/**
 * API extension for source code management integrations.
 *
 * Provides {@link ScmIntegrationsApi} backed by the app config, enabling
 * SCM-aware features (e.g. inline source links) for catalog entities.
 *
 * Registered in this module because SCM integration is a cross-cutting
 * app-level concern with no owning internal plugin.
 */
const scmIntegrationsApi = ApiBlueprint.make({
    name: "scm-integrations",
    params: defineParams =>
        defineParams(
            createApiFactory({
                api: scmIntegrationsApiRef,
                deps: { configApi: configApiRef },
                factory: ({ configApi }) =>
                    ScmIntegrationsApi.fromConfig(configApi),
            })
        ),
});

/**
 * API extension for multi-provider SCM authentication.
 *
 * Registers the default {@link ScmAuth} factory which delegates auth to the
 * appropriate provider based on the SCM host (GitHub, GitLab, Bitbucket, etc.).
 */
const scmAuthApi = ApiBlueprint.make({
    name: "scm-auth",
    params: defineParams => defineParams(ScmAuth.createDefaultApiFactory()),
});

/**
 * API extension for GitHub authentication.
 *
 * Overrides the default GitHub OAuth API to enforce the custom default scopes
 * required by Banno's integrations (`read:user`, `read:org`, `repo`).
 */
const githubAuthApi = ApiBlueprint.make({
    name: "github-auth",
    params: defineParams =>
        defineParams(
            createApiFactory({
                api: githubAuthApiRef,
                deps: {
                    discoveryApi: discoveryApiRef,
                    oauthRequestApi: oauthRequestApiRef,
                    configApi: configApiRef,
                },
                factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
                    GithubAuth.create({
                        configApi,
                        discoveryApi,
                        oauthRequestApi,
                        defaultScopes: ["read:user", "read:org", "repo"],
                        environment:
                            configApi.getOptionalString("auth.environment"),
                    }),
            })
        ),
});

/**
 * Frontend module that overrides the app plugin's nav content and sign-in page
 * with Banno-specific implementations, and registers cross-cutting
 * app-level API factories that have no owning internal plugin.
 *
 * Migrated from `packages/app/src/modules/nav/navModule.ts`, the legacy
 * `AuthPage`/`convertLegacyAppOptions` sign-in setup in `App.tsx`, and the
 * SCM/GitHub API registrations from the former `app-module-apis`.
 */
const appModuleNav = createFrontendModule({
    pluginId: "app",
    extensions: [
        appLayout,
        SidebarNavContent,
        signInPage,
        scmIntegrationsApi,
        scmAuthApi,
        githubAuthApi,
    ],
});

export default appModuleNav;
